import { TimescapeOptions } from "@formant/core/model/IAggregateTimeRange";
import { IFilter } from "@formant/core/model/IFilter";
import { ITagSets } from "@formant/core/model/ITagSets";
import { ILabelValuePair } from "@formant/rectangle-sdk/src/store/store.types";

import { FeatureFlagLocalStorageManager } from "../../../utils/FeatureFlagLocalStorageManager/FeatureFlagLocalStorageManager";
import { FeatureFlagsLocalStorageEnum } from "../../../utils/FeatureFlagLocalStorageManager/FeatureFlagsLocalStorage.enum";

export interface IUrlParameters {
    // Time-related
    startDate: Date | null;
    endDate: Date | null;
    // Aggregate time-related
    aggregateStartDate: Date | null;
    aggregateEndDate: Date | null;
    aggregateTimescale: TimescapeOptions | null;
    // State-related
    isPlaying: boolean | null;
    isLive: boolean | null;
    // Configuration-related
    timeRange: ILabelValuePair | null;
    playbackSpeed: ILabelValuePair | null;
    seekBy: ILabelValuePair | null;
    // Tags
    tags: string[] | null;
    // New parameters
    globalFilter: IFilter | null;
    selectedTags: ITagSets | null;
    // Selected date
    selectedDate: Date | null;
}

interface IParamConfig {
    enabled: boolean;
    validate?: (value: string) => boolean;
}

export class UrlParamService {
    private static enabled =
        FeatureFlagLocalStorageManager.getFlag(
            FeatureFlagsLocalStorageEnum.EnableUrlParamSync,
        ) ?? false;

    private static PARAM_KEYS = {
        START_DATE: "startDate",
        END_DATE: "endDate",
        AGGREGATE_START_DATE: "aggregateStartDate",
        AGGREGATE_END_DATE: "aggregateEndDate",
        AGGREGATE_TIMESCALE: "aggregateTimescale",
        IS_PLAYING: "isPlaying",
        IS_LIVE: "isLive",
        TIME_RANGE: "timeRange",
        PLAYBACK_SPEED: "playbackSpeed",
        SEEK_BY: "seekBy",
        TAGS: "tags",
        GLOBAL_FILTER: "globalFilter",
        SELECTED_TAGS: "selectedTags",
        SELECTED_DATE: "selectedDate",
    };

    private static paramConfig: Record<string, IParamConfig> = {
        [UrlParamService.PARAM_KEYS.START_DATE]: {
            enabled: true,
            validate: (value: string) => !isNaN(new Date(value).getTime()),
        },
        [UrlParamService.PARAM_KEYS.END_DATE]: {
            enabled: true,
            validate: (value: string) => !isNaN(new Date(value).getTime()),
        },
        [UrlParamService.PARAM_KEYS.AGGREGATE_START_DATE]: {
            enabled: true,
            validate: (value: string) => !isNaN(new Date(value).getTime()),
        },
        [UrlParamService.PARAM_KEYS.AGGREGATE_END_DATE]: {
            enabled: true,
            validate: (value: string) => !isNaN(new Date(value).getTime()),
        },
        [UrlParamService.PARAM_KEYS.AGGREGATE_TIMESCALE]: {
            enabled: true,
            validate: (value: string) =>
                ["1d", "1w", "1m", "3m", "6m", "1y"].includes(value),
        },
        [UrlParamService.PARAM_KEYS.IS_PLAYING]: {
            enabled: true,
            validate: (value: string) => value === "true" || value === "false",
        },
        [UrlParamService.PARAM_KEYS.IS_LIVE]: {
            enabled: true,
            validate: (value: string) => value === "true" || value === "false",
        },
        [UrlParamService.PARAM_KEYS.TIME_RANGE]: {
            enabled: true,
            validate: (value: string) => {
                try {
                    return JSON.parse(value) !== null;
                } catch {
                    return false;
                }
            },
        },
        [UrlParamService.PARAM_KEYS.PLAYBACK_SPEED]: {
            enabled: true,
            validate: (value: string) => {
                try {
                    return JSON.parse(value) !== null;
                } catch {
                    return false;
                }
            },
        },
        [UrlParamService.PARAM_KEYS.SEEK_BY]: {
            enabled: true,
            validate: (value: string) => {
                try {
                    return JSON.parse(value) !== null;
                } catch {
                    return false;
                }
            },
        },
        [UrlParamService.PARAM_KEYS.TAGS]: {
            enabled: true,
            validate: (value: string) => {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed);
                } catch {
                    return false;
                }
            },
        },
        [UrlParamService.PARAM_KEYS.GLOBAL_FILTER]: {
            enabled: true,
            validate: (value: string) => {
                try {
                    const parsed = JSON.parse(value);
                    if (!parsed || typeof parsed !== "object") {
                        return false;
                    }

                    // Validate each property only if it exists
                    if (
                        parsed.deviceIds !== undefined &&
                        !Array.isArray(parsed.deviceIds)
                    ) {
                        return false;
                    }
                    if (
                        parsed.tags !== undefined &&
                        typeof parsed.tags !== "object"
                    ) {
                        return false;
                    }
                    if (
                        parsed.names !== undefined &&
                        !Array.isArray(parsed.names)
                    ) {
                        return false;
                    }
                    if (
                        parsed.types !== undefined &&
                        !Array.isArray(parsed.types)
                    ) {
                        return false;
                    }

                    return true;
                } catch {
                    return false;
                }
            },
        },
        [UrlParamService.PARAM_KEYS.SELECTED_TAGS]: {
            enabled: true,
            validate: (value: string) => {
                try {
                    const parsed = JSON.parse(value);
                    return parsed && typeof parsed === "object";
                } catch {
                    return false;
                }
            },
        },
        [UrlParamService.PARAM_KEYS.SELECTED_DATE]: {
            enabled: true,
            validate: (value: string) => !isNaN(new Date(value).getTime()),
        },
    };

    static setEnabled(value: boolean): void {
        this.enabled = value;
        FeatureFlagLocalStorageManager.setFlag(
            FeatureFlagsLocalStorageEnum.EnableUrlParamSync,
            value,
        );
        if (!value) {
            const url = new URL(window.location.href);
            const cleanUrl = `${url.origin}${url.pathname}`;
            window.history.pushState({}, "", cleanUrl);
        }
    }

    static isEnabled(): boolean {
        return (
            this.enabled &&
            (FeatureFlagLocalStorageManager.getFlag(
                FeatureFlagsLocalStorageEnum.EnableUrlParamSync,
            ) ??
                false)
        );
    }

    static setParamEnabled(param: string, enabled: boolean): void {
        if (!this.enabled) {
            return;
        }

        if (this.paramConfig[param]) {
            this.paramConfig[param].enabled = enabled;
            if (!enabled) {
                const url = new URL(window.location.href);
                url.searchParams.delete(param);
                window.history.pushState({}, "", url);
            }
        }
    }

    private static safelyParseJSON(value: string | null) {
        if (!value) {
            return null;
        }
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }

    private static updateParam(
        param: string,
        value: string | number | boolean | object | undefined,
        stringify = false,
    ): void {
        if (!this.enabled || !this.paramConfig[param]?.enabled) {
            return;
        }

        const url = new URL(window.location.href);
        const stringValue = stringify ? JSON.stringify(value) : String(value);
        if (this.paramConfig[param]?.validate?.(stringValue)) {
            url.searchParams.set(param, stringValue);
            window.history.pushState({}, "", url);
        }
    }

    static updateParams(params: Partial<IUrlParameters>): void {
        if (!this.enabled) {
            return;
        }

        const {
            startDate,
            endDate,
            aggregateStartDate,
            aggregateEndDate,
            aggregateTimescale,
            isPlaying,
            isLive,
            timeRange,
            playbackSpeed,
            seekBy,
            tags,
            globalFilter,
            selectedTags,
            selectedDate,
        } = params;

        if (startDate) {
            this.updateParam(
                this.PARAM_KEYS.START_DATE,
                startDate.toISOString(),
            );
        }
        if (endDate) {
            this.updateParam(this.PARAM_KEYS.END_DATE, endDate.toISOString());
        }
        if (aggregateStartDate) {
            this.updateParam(
                this.PARAM_KEYS.AGGREGATE_START_DATE,
                aggregateStartDate.toISOString(),
            );
        }
        if (aggregateEndDate) {
            this.updateParam(
                this.PARAM_KEYS.AGGREGATE_END_DATE,
                aggregateEndDate.toISOString(),
            );
        }
        if (aggregateTimescale) {
            this.updateParam(
                this.PARAM_KEYS.AGGREGATE_TIMESCALE,
                aggregateTimescale,
            );
        }
        if (isPlaying !== null) {
            this.updateParam(this.PARAM_KEYS.IS_PLAYING, isPlaying);
        }
        if (isLive !== null) {
            this.updateParam(this.PARAM_KEYS.IS_LIVE, isLive);
        }
        if (timeRange) {
            this.updateParam(this.PARAM_KEYS.TIME_RANGE, timeRange, true);
        }
        if (playbackSpeed) {
            this.updateParam(
                this.PARAM_KEYS.PLAYBACK_SPEED,
                playbackSpeed,
                true,
            );
        }
        if (seekBy) {
            this.updateParam(this.PARAM_KEYS.SEEK_BY, seekBy, true);
        }
        if (tags) {
            this.updateParam(this.PARAM_KEYS.TAGS, tags, true);
        }
        if (globalFilter) {
            this.updateParam(this.PARAM_KEYS.GLOBAL_FILTER, globalFilter, true);
        }
        if (selectedTags) {
            this.updateParam(this.PARAM_KEYS.SELECTED_TAGS, selectedTags, true);
        }
        if (selectedDate) {
            this.updateParam(
                this.PARAM_KEYS.SELECTED_DATE,
                selectedDate.toISOString(),
            );
        }
    }

    static getParams(): IUrlParameters {
        if (!this.enabled) {
            return {
                startDate: null,
                endDate: null,
                aggregateStartDate: null,
                aggregateEndDate: null,
                aggregateTimescale: null,
                isPlaying: null,
                isLive: null,
                timeRange: null,
                playbackSpeed: null,
                seekBy: null,
                tags: null,
                globalFilter: null,
                selectedTags: null,
                selectedDate: null,
            };
        }

        const url = new URL(window.location.href);
        const params: IUrlParameters = {
            startDate: null,
            endDate: null,
            aggregateStartDate: null,
            aggregateEndDate: null,
            aggregateTimescale: null,
            isPlaying: null,
            isLive: null,
            timeRange: null,
            playbackSpeed: null,
            seekBy: null,
            tags: null,
            globalFilter: null,
            selectedTags: null,
            selectedDate: null,
        };

        try {
            // Parse date parameters
            const startDateStr = url.searchParams.get(
                this.PARAM_KEYS.START_DATE,
            );
            if (
                startDateStr &&
                this.paramConfig[this.PARAM_KEYS.START_DATE].validate?.(
                    startDateStr,
                )
            ) {
                params.startDate = new Date(startDateStr);
            }

            const endDateStr = url.searchParams.get(this.PARAM_KEYS.END_DATE);
            if (
                endDateStr &&
                this.paramConfig[this.PARAM_KEYS.END_DATE].validate?.(
                    endDateStr,
                )
            ) {
                params.endDate = new Date(endDateStr);
            }

            // Parse aggregate date parameters
            const aggregateStartDateStr = url.searchParams.get(
                this.PARAM_KEYS.AGGREGATE_START_DATE,
            );
            if (
                aggregateStartDateStr &&
                this.paramConfig[
                    this.PARAM_KEYS.AGGREGATE_START_DATE
                ].validate?.(aggregateStartDateStr)
            ) {
                params.aggregateStartDate = new Date(aggregateStartDateStr);
            }

            const aggregateEndDateStr = url.searchParams.get(
                this.PARAM_KEYS.AGGREGATE_END_DATE,
            );
            if (
                aggregateEndDateStr &&
                this.paramConfig[this.PARAM_KEYS.AGGREGATE_END_DATE].validate?.(
                    aggregateEndDateStr,
                )
            ) {
                params.aggregateEndDate = new Date(aggregateEndDateStr);
            }

            // Parse timescale
            const timescaleStr = url.searchParams.get(
                this.PARAM_KEYS.AGGREGATE_TIMESCALE,
            );
            if (
                timescaleStr &&
                this.paramConfig[
                    this.PARAM_KEYS.AGGREGATE_TIMESCALE
                ].validate?.(timescaleStr)
            ) {
                params.aggregateTimescale = timescaleStr as TimescapeOptions;
            }

            // Parse boolean parameters
            const isPlayingStr = url.searchParams.get(
                this.PARAM_KEYS.IS_PLAYING,
            );
            if (
                isPlayingStr &&
                this.paramConfig[this.PARAM_KEYS.IS_PLAYING].validate?.(
                    isPlayingStr,
                )
            ) {
                params.isPlaying = isPlayingStr === "true";
            }

            const isLiveStr = url.searchParams.get(this.PARAM_KEYS.IS_LIVE);
            if (
                isLiveStr &&
                this.paramConfig[this.PARAM_KEYS.IS_LIVE].validate?.(isLiveStr)
            ) {
                params.isLive = isLiveStr === "true";
            }

            // Parse JSON parameters
            const timeRangeStr = url.searchParams.get(
                this.PARAM_KEYS.TIME_RANGE,
            );
            if (
                timeRangeStr &&
                this.paramConfig[this.PARAM_KEYS.TIME_RANGE].validate?.(
                    timeRangeStr,
                )
            ) {
                params.timeRange = this.safelyParseJSON(timeRangeStr);
            }

            const playbackSpeedStr = url.searchParams.get(
                this.PARAM_KEYS.PLAYBACK_SPEED,
            );
            if (
                playbackSpeedStr &&
                this.paramConfig[this.PARAM_KEYS.PLAYBACK_SPEED].validate?.(
                    playbackSpeedStr,
                )
            ) {
                params.playbackSpeed = this.safelyParseJSON(playbackSpeedStr);
            }

            const seekByStr = url.searchParams.get(this.PARAM_KEYS.SEEK_BY);
            if (
                seekByStr &&
                this.paramConfig[this.PARAM_KEYS.SEEK_BY].validate?.(seekByStr)
            ) {
                params.seekBy = this.safelyParseJSON(seekByStr);
            }

            // Parse tags
            const tagsStr = url.searchParams.get(this.PARAM_KEYS.TAGS);
            if (
                tagsStr &&
                this.paramConfig[this.PARAM_KEYS.TAGS].validate?.(tagsStr)
            ) {
                params.tags = this.safelyParseJSON(tagsStr);
            }

            // Parse global filter
            const globalFilterStr = url.searchParams.get(
                this.PARAM_KEYS.GLOBAL_FILTER,
            );
            if (
                globalFilterStr &&
                this.paramConfig[this.PARAM_KEYS.GLOBAL_FILTER].validate?.(
                    globalFilterStr,
                )
            ) {
                params.globalFilter = this.safelyParseJSON(globalFilterStr);
            }

            // Parse selected tags
            const selectedTagsStr = url.searchParams.get(
                this.PARAM_KEYS.SELECTED_TAGS,
            );
            if (
                selectedTagsStr &&
                this.paramConfig[this.PARAM_KEYS.SELECTED_TAGS].validate?.(
                    selectedTagsStr,
                )
            ) {
                params.selectedTags = this.safelyParseJSON(selectedTagsStr);
            }

            // Parse selected date
            const selectedDateStr = url.searchParams.get(
                this.PARAM_KEYS.SELECTED_DATE,
            );
            if (
                selectedDateStr &&
                this.paramConfig[this.PARAM_KEYS.SELECTED_DATE].validate?.(
                    selectedDateStr,
                )
            ) {
                params.selectedDate = new Date(selectedDateStr);
            }
        } catch (error) {
            console.error("Error parsing URL parameters:", error);
        }

        return params;
    }

    static removeParams(params: string[]): void {
        if (!this.enabled) {
            return;
        }

        const url = new URL(window.location.href);
        params.forEach(param => {
            url.searchParams.delete(param);
        });
        window.history.pushState({}, "", url);
    }

    static clearParams(): void {
        if (!this.enabled) {
            return;
        }

        const url = new URL(window.location.href);
        Object.entries(this.paramConfig).forEach(([param, config]) => {
            if (config.enabled) {
                url.searchParams.delete(param);
            }
        });
        window.history.pushState({}, "", url);
    }
}
